import { createContext, useContext, useMemo } from 'react';
import { evaluateEntitlement, subscriptionCatalog } from '@tabbio/utils';
import { applyPaywallBypass } from '../lib/dev-paywall-bypass';
import { reportMissingProvider } from '../lib/missing-provider';
import type {
  AIActionKey,
  CompanySeatAssignmentsResult,
  EntitlementDecision,
  FeatureKey,
  SubscriptionCatalog,
  SubscriptionSnapshot,
  UpdateCompanySeatInput,
} from '@tabbio/types';

export interface SubscriptionContextValue {
  catalog: SubscriptionCatalog;
  snapshot: SubscriptionSnapshot | null;
  seats: CompanySeatAssignmentsResult | null;
  isLoading: boolean;
  error: Error | null;
  invalidateSubscription: () => Promise<void>;
  refetchSubscription: () => Promise<unknown>;
  refetchSeats: () => Promise<unknown>;
  beginOptimisticAction: (units?: number) => void;
  clearOptimisticActions: () => void;
  updateSeatAssignment: (
    userId: string,
    input: UpdateCompanySeatInput,
  ) => Promise<CompanySeatAssignmentsResult>;
}

export const SubscriptionContext =
  createContext<SubscriptionContextValue | null>(null);

// Loading-shaped fallback for frames rendered outside SubscriptionProvider
// (auth/route teardown transitions). Mirrors WorkspaceContext's default-value
// pattern: consumers see a loading state instead of crashing the whole app to
// the root error boundary. Only the mutation throws, and only when invoked.
const fallbackSubscriptionContextValue: SubscriptionContextValue = {
  catalog: subscriptionCatalog as SubscriptionCatalog,
  snapshot: null,
  seats: null,
  isLoading: true,
  error: null,
  invalidateSubscription: async () => undefined,
  refetchSubscription: async () => undefined,
  refetchSeats: async () => undefined,
  beginOptimisticAction: () => undefined,
  clearOptimisticActions: () => undefined,
  updateSeatAssignment: async () => {
    throw new Error('Subscription state is unavailable. Please try again.');
  },
};

export function useSubscriptionState() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    reportMissingProvider('SubscriptionProvider');
    return fallbackSubscriptionContextValue;
  }

  return context;
}

function buildLoadingDecision(
  key: FeatureKey | AIActionKey,
): EntitlementDecision {
  const isAiAction = Object.prototype.hasOwnProperty.call(
    subscriptionCatalog.aiActions,
    key,
  );

  return {
    allowed: false,
    ...(isAiAction
      ? { actionKey: key as AIActionKey }
      : { featureKey: key as FeatureKey }),
    reasonCode: 'loading',
    message: 'Subscription state is loading.',
    promptKey: null,
    recommendedPlanSet: null,
    recommendedTier: null,
    remainingActionsToday: null,
    remainingCreditsToday: null,
    remainingCreditsThisCycle: null,
    estimatedCredits: null,
    limitBehavior: 'none',
    meterSubject: 'none',
    modelPolicy: 'sonnet_only',
  };
}

export function useEntitlement(
  key: FeatureKey | AIActionKey,
  options?: {
    complexity?: 'standard' | 'complex';
    subject?: 'feature' | 'ai_action';
  },
) {
  const { catalog, snapshot } = useSubscriptionState();

  return useMemo(() => {
    const decision = snapshot
      ? evaluateEntitlement(snapshot, key, catalog, options)
      : buildLoadingDecision(key);

    return applyPaywallBypass(decision);
  }, [catalog, key, options, snapshot]);
}

export function useAiActionGuard(
  actionKey: AIActionKey,
  options?: { complexity?: 'standard' | 'complex' },
) {
  const {
    beginOptimisticAction,
    clearOptimisticActions,
    error,
    invalidateSubscription,
    isLoading,
  } = useSubscriptionState();
  const decision = useEntitlement(actionKey, {
    ...options,
    subject: 'ai_action',
  });

  return {
    decision,
    remainingActionsToday: decision.remainingActionsToday,
    remainingCreditsToday: decision.remainingCreditsToday ?? null,
    remainingCreditsThisCycle: decision.remainingCreditsThisCycle ?? null,
    estimatedCredits: decision.estimatedCredits ?? 1,
    canProceed: decision.allowed,
    isLoading,
    error,
    beginOptimisticAction,
    clearOptimisticActions,
    reconcile: invalidateSubscription,
  };
}

export function useCompanySeatAssignments() {
  const { seats, updateSeatAssignment, refetchSeats } = useSubscriptionState();

  return {
    seats,
    updateSeatAssignment,
    refetchSeats,
  };
}
